import { DataSourceType } from '@/types/settings.ts';

import { ResponseStatus } from './base';
import { AxiosBaseResponse } from './common';

export interface GetPostSuccessResponse extends AxiosBaseResponse {
  data: Post;
}

export interface GetPostsSuccessResponse {
  status: ResponseStatus;
  data: {
    posts: Post[];
  };
}

export interface FormatPostSuccessResponse extends AxiosBaseResponse {
  data: {
    task: Task;
  };
}

export interface Task {
  id: string;
  status: string;
  error_message: string;
  error_code: string;
}

export interface Post {
  id: string;
  created: string;
  title: string;
  content: string;
  content_html: string;
  original_idea: string;
  images: [
    {
      id: string;
      url: string;
      status: ResponseStatus;
    },
  ];
  available_formats: SocialChannels[];
}

export enum SocialChannels {
  LinkedIn = 'LINKEDIN',
  Facebook = 'FACEBOOK',
  Instagram = 'INSTAGRAM',
}

export const compareSocialIds = (
  dataSourceId: DataSourceType,
  socialChannel: SocialChannels
) => {
  switch (socialChannel) {
    case SocialChannels.LinkedIn:
      return dataSourceId === DataSourceType.LINKEDIN;
    case SocialChannels.Facebook:
      return dataSourceId === DataSourceType.FACEBOOK;
    case SocialChannels.Instagram:
      return dataSourceId === DataSourceType.INSTAGRAM;
    default:
      return false;
  }
};

export const getSocialNameByValue = (val: string) => {
  // Map the enum values to their display names
  const displayNames: Record<string, string> = {
    FACEBOOK: 'Facebook',
    INSTAGRAM: 'Instagram',
    LINKEDIN: 'LinkedIn',
  };

  return displayNames[val] || val;
};

export interface FormatPostDto {
  projectId: string;
  postId: string;
  formats: SocialChannels[];
}

export interface FormattedPut extends FormattedPost {
  post_id: string;
  title: string;
}
export interface GetFormattedPostSuccessResponse extends AxiosBaseResponse {
  data: FormattedPost;
}
export interface GetFormattedPutResponse extends AxiosBaseResponse {
  data: FormattedPut;
}

export interface FormattedPost {
  post_format: string;
  social_media_type: string;
  title: string;
  content: string;
  content_html: string;
  images: [
    {
      id: string;
      url: string;
      status: ResponseStatus;
    },
  ];
}
export interface UpdatePostSuccessResponse {
  error_code: string;
  error_message: string;
  error_details: [];
  data: Post;
}

export enum MediaType {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  TEXT = 'TEXT',
}

export enum FacebookContentType {
  POST = 'POST',
  LINK = 'LINK',
  IMAGE = 'IMAGE',
}

export interface BasePublishRequest {
  publish_at?: string;
}

export interface InstagramPublishRequest extends BasePublishRequest {
  resource_url: string;
  media_type: MediaType;
}

export interface FacebookPublishRequest extends BasePublishRequest {
  content_type: FacebookContentType;
  content_url?: string; // Optional
}

// can just use the base request since it only requires publish_at
export type LinkedInPublishRequest = BasePublishRequest;

export interface PublishPostResponse extends AxiosBaseResponse {
  data: {
    task: Task;
  };
}

export const formattedPostStatuses = [
  'DRAFT',
  'SCHEDULED',
  'PUBLISHED',
  'PENDING',
  'FAILED',
] as const;

export type FormattedPostStatus = (typeof formattedPostStatuses)[number];
